import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { isStopTranscript } from '@utils/charting-voice-transcript.utils'

export type VoiceTranscriptStatus = 'pending' | 'applied' | 'info' | 'failed'

export interface VoiceTranscriptEntry {
  id: string
  text: string
  status: VoiceTranscriptStatus
  note?: string
  heard?: string
}

export interface VoiceTranscriptFinalizeOptions {
  status?: VoiceTranscriptStatus
  awaitResult?: boolean
  heard?: string
}

export interface VoiceTranscriptAnnotation {
  id?: string
  note?: string
  status: VoiceTranscriptStatus
  /** Only update an entry that has no outcome yet. */
  pendingOnly?: boolean
}

export interface VoiceTranscriptSnapshot {
  history: VoiceTranscriptEntry[]
  recognized: string
  liveText: string
  queued: string[]
}

export interface VoiceTranscriptFeed {
  history: VoiceTranscriptEntry[]
  recognized: string
  liveText: string
  queued: string[]
  partial: (itemId: string, text: string) => void
  finalize: (
    itemId: string,
    text: string,
    options?: VoiceTranscriptFinalizeOptions,
  ) => void
  annotate: (update: VoiceTranscriptAnnotation) => string | undefined
  drop: (itemId: string) => void
  discard: () => void
  reset: () => void
  subscribe: (listener: () => void) => () => void
  getSnapshot: () => VoiceTranscriptSnapshot
}

const HISTORY_LIMIT = 5
const PARTIAL_THROTTLE_MS = 80
const EMPTY_SNAPSHOT: VoiceTranscriptSnapshot = {
  history: [],
  recognized: '',
  liveText: '',
  queued: [],
}

const comparableHeard = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[.,;:!?\u2019']/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')

const emptySubscribe = (): (() => void) => (): void => undefined

const emptySnapshot = (): VoiceTranscriptSnapshot => EMPTY_SNAPSHOT

export interface VoiceTranscriptSource {
  history: VoiceTranscriptEntry[]
  liveText: string
  queued: string[]
  recognized?: string
  subscribe?: (listener: () => void) => () => void
  getSnapshot?: () => VoiceTranscriptSnapshot
}

/** Subscribe to transcript updates without rendering the component that owns the voice session. */
export function useVoiceTranscriptSnapshot(
  source: VoiceTranscriptSource,
): VoiceTranscriptSnapshot {
  const subscribed = useSyncExternalStore(
    source.subscribe ?? emptySubscribe,
    source.getSnapshot ?? emptySnapshot,
    source.getSnapshot ?? emptySnapshot,
  )
  if (source.subscribe && source.getSnapshot) {
    return subscribed
  }
  const recognized =
    source.recognized ?? source.history[source.history.length - 1]?.text ?? ''
  return {
    history: source.history,
    recognized,
    liveText: source.liveText,
    queued: source.queued,
  }
}

/** Tracks speech per item so an older phrase never overwrites or clears a newer one. */
export function useVoiceTranscriptFeed(
  format: (text: string) => string = (text): string => text.trim(),
): VoiceTranscriptFeed {
  const formatRef = useRef(format)
  formatRef.current = format
  const partials = useRef(new Map<string, string>())
  const finished = useRef(new Set<string>())
  const awaiting = useRef<string[]>([])
  const lastPublish = useRef(0)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  const snapshotRef = useRef<VoiceTranscriptSnapshot>(EMPTY_SNAPSHOT)
  const listeners = useRef(new Set<() => void>())

  const commit = useCallback((next: VoiceTranscriptSnapshot): void => {
    const current = snapshotRef.current
    if (
      current.history === next.history &&
      current.recognized === next.recognized &&
      current.liveText === next.liveText &&
      current.queued === next.queued
    ) {
      return
    }
    snapshotRef.current = next
    listeners.current.forEach((listener) => {
      listener()
    })
  }, [])

  const publish = useCallback((): void => {
    clearTimeout(timer.current)
    timer.current = undefined
    lastPublish.current = Date.now()
    const texts = Array.from(partials.current.values())
      .map((text) => formatRef.current(text))
      .filter(Boolean)
    const text = texts[texts.length - 1] ?? ''
    const queued = texts.slice(0, -1)
    const current = snapshotRef.current
    if (
      current.liveText === text &&
      current.queued.join('\n') === queued.join('\n')
    ) {
      return
    }
    commit({
      ...current,
      liveText: text,
      queued,
    })
  }, [commit])

  const updateHistory = useCallback(
    (
      updater: (entries: VoiceTranscriptEntry[]) => VoiceTranscriptEntry[],
    ): void => {
      const history = updater(snapshotRef.current.history)
      commit({
        ...snapshotRef.current,
        history,
        recognized: history[history.length - 1]?.text ?? '',
      })
    },
    [commit],
  )

  useEffect(() => (): void => clearTimeout(timer.current), [])

  const partial = useCallback(
    (itemId: string, text: string): void => {
      if (finished.current.has(itemId)) return
      const isNew = !partials.current.has(itemId)
      partials.current.set(itemId, text)
      const elapsed = Date.now() - lastPublish.current
      if (isNew || elapsed >= PARTIAL_THROTTLE_MS) publish()
      else if (!timer.current) {
        timer.current = setTimeout(publish, PARTIAL_THROTTLE_MS - elapsed)
      }
    },
    [publish],
  )

  const finalize = useCallback(
    (
      itemId: string,
      text: string,
      options: VoiceTranscriptFinalizeOptions = {},
    ): void => {
      if (finished.current.has(itemId) && !partials.current.has(itemId)) return
      finished.current.add(itemId)
      partials.current.delete(itemId)
      publish()
      const trimmed = text.trim()
      const status = options.status ?? 'pending'
      const awaitResult = options.awaitResult ?? true
      const resolved = isStopTranscript(trimmed) ? 'applied' : status
      if (resolved === 'pending' && awaitResult) awaiting.current.push(itemId)
      const heard =
        options.heard &&
        comparableHeard(options.heard) !== comparableHeard(trimmed)
          ? options.heard.trim()
          : undefined
      updateHistory((entries) =>
        [
          ...entries.filter((entry) => entry.id !== itemId),
          {
            id: itemId,
            text: trimmed,
            status: resolved,
            ...(heard ? { heard } : {}),
          },
        ].slice(-HISTORY_LIMIT),
      )
    },
    [publish, updateHistory],
  )

  const annotate = useCallback(
    ({
      id,
      note,
      status,
      pendingOnly = false,
    }: VoiceTranscriptAnnotation): string | undefined => {
      const target = id ?? awaiting.current[0]
      if (!target) return undefined
      awaiting.current = awaiting.current.filter((entry) => entry !== target)
      updateHistory((entries) =>
        entries.map((entry) =>
          entry.id === target && (!pendingOnly || entry.status === 'pending')
            ? { ...entry, status, note: note ?? entry.note }
            : entry,
        ),
      )
      return target
    },
    [updateHistory],
  )

  const drop = useCallback(
    (itemId: string): void => {
      finished.current.add(itemId)
      if (partials.current.delete(itemId)) publish()
    },
    [publish],
  )

  const discard = useCallback((): void => {
    partials.current.forEach((_, itemId) => finished.current.add(itemId))
    partials.current.clear()
    const dropped = new Set(awaiting.current)
    awaiting.current = []
    publish()
    if (dropped.size === 0) return
    updateHistory((entries) =>
      entries.map((entry) =>
        dropped.has(entry.id)
          ? {
              ...entry,
              status: 'info',
              note: 'Discarded before it was applied.',
            }
          : entry,
      ),
    )
  }, [publish, updateHistory])

  const reset = useCallback((): void => {
    partials.current.clear()
    finished.current.clear()
    awaiting.current = []
    updateHistory(() => [])
    publish()
  }, [publish, updateHistory])

  const subscribe = useCallback((listener: () => void): (() => void) => {
    listeners.current.add(listener)
    return (): void => {
      listeners.current.delete(listener)
    }
  }, [])

  const getSnapshot = useCallback(
    (): VoiceTranscriptSnapshot => snapshotRef.current,
    [],
  )

  const feedRef = useRef<VoiceTranscriptFeed>()
  if (!feedRef.current) {
    feedRef.current = {
      get history(): VoiceTranscriptEntry[] {
        return snapshotRef.current.history
      },
      get recognized(): string {
        return snapshotRef.current.recognized
      },
      get liveText(): string {
        return snapshotRef.current.liveText
      },
      get queued(): string[] {
        return snapshotRef.current.queued
      },
      partial,
      finalize,
      annotate,
      drop,
      discard,
      reset,
      subscribe,
      getSnapshot,
    }
  }

  return feedRef.current
}
